import type { BulkVoucherCmd } from "@/data/commands/table/BulkVoucherCmd";
import { Factory } from "@/data/dao/Factory";
import { VoucherDao } from "@/data/dao/service/VoucherDao";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { UiNotification } from "@/portal/ui/Notification";

export class VoucherBulkWindow extends BaseModal {
    public onExecuted?: { () };
    private $form: JQuery;

    private $formSection: JQuery;

    private valueField?: NumberInput;
    private voucherQtyField?: NumberInput;
    private $internalNoteField?: JQuery;
    private $nameConvField?: JQuery;
    private $submitBtn: JQuery;

    public needsTableReload = false;

    constructor() {
        super(i18n("modal.voucherBulk.title"), "modal-lg", false);
        this.$element.addClass("voucher-bulk");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        this.$formSection = $('<div class="kt-section kt-section--last"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $(
            '<button type="button" class="btn btn-success">' + i18n("modal.voucherBulk.create") + "</button>"
        )
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.showCreateModify();
    }

    protected onShow() {
        this.reset();
    }

    protected onHide() {}

    private reset() {
        this.valueField?.setValue(undefined);
        this.$nameConvField?.val("");
        this.voucherQtyField?.setValue(undefined);
        this.$internalNoteField?.val("");
    }

    private onSubmit() {
        const bulkCommand = this.getCommandDto();
        if (bulkCommand === undefined) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        Factory.getInstance<VoucherDao>(VoucherDao)
            .bulk(bulkCommand)
            .then(() => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                this.reset();
                this.close();
                if (this.onExecuted !== undefined) this.onExecuted();
            })
            .catch((ex) => {
                logError(ex);
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                const exception = i18n("modal.tableBulk.failedEdit");
                UiNotification.showException(exception);
            });
    }

    private getCommandDto(): BulkVoucherCmd {
        let hasError = false;

        const value = this.valueField!.getValue();
        const internalNote = this.$internalNoteField?.val();
        const voucherQty = this.voucherQtyField!.getValue();

        if (value === undefined || value <= 0) {
            hasError = true;
            this.valueField?.setError(true);
        }

        if (voucherQty === undefined || voucherQty <= 0) {
            hasError = true;
            this.voucherQtyField?.setError(true);
        }

        if (internalNote != null && internalNote.length > 25) {
            hasError = true;
            this.$internalNoteField?.addClass("is-invalid");
        } else {
            this.$internalNoteField?.removeClass("is-invalid");
        }

        if (hasError) return undefined!;

        const bulkCommand: BulkVoucherCmd = {
            value: value!,
            voucherQty: voucherQty!,
            internalNote: internalNote
        };

        return bulkCommand;
    }

    private showCreateModify() {
        this.$formSection.empty();

        // Value
        const valueRow = $('<div class="form-group row"></div>').appendTo(this.$formSection);
        valueRow.append(
            `<label class="col-xl-4 col-form-label">${i18n("modal.voucherBulk.value")} <span class="mandatory">*</span></label>`
        );
        const valueRowCol = $('<div class="col-3" />').appendTo(valueRow);

        this.valueField = new NumberInput(
            $('<input type="text" name="value-input" class="form-control"/>').appendTo(valueRowCol),
            {
                min: 0,
                step: 1,
                decimals: 2,
            }
        );

        // Value
        const voucherQtyRow = $('<div class="form-group row"></div>').appendTo(this.$formSection);
        voucherQtyRow.append(
            `<label class="col-xl-4 col-form-label">${i18n("modal.voucherBulk.voucherQty")} <span class="mandatory">*</span></label>`
        );
        const voucherQtyRowCol = $('<div class="col-3" />').appendTo(voucherQtyRow);

        this.voucherQtyField = new NumberInput(
            $('<input type="text" name="voucher-qty-input" class="form-control"/>').appendTo(voucherQtyRowCol)
        );

        // internal note
        const internalNoteRow = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.voucherBulk.internalNote") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(this.$formSection)
            .find("> .col-xl-6");

        const internalNoteRowGroup = $(
            '<div class="input-group"><input type="text" name="internal-note-field" class="form-control" ></div>'
        ).appendTo(internalNoteRow);

        this.$internalNoteField = internalNoteRowGroup.find('input[name="internal-note-field"]');
        this.$form.unbind("submit").bind("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();

            return false;
        });
    }
}
